import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getAppLocale } from '@/lib/locale'
import { AlertTriangleIcon, InfoIcon, UserCheck, UserX } from 'lucide-react'

import type { ApiError } from '@/lib/api'
import type { AgentRecord } from '@/types/agents'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'

export type AgentLifecycleAction = 'suspend' | 'reactivate'

const COUNTDOWN_SECONDS = 10

export function AgentLifecycleConfirmDialog({
  action,
  isSubmitting,
  error,
  onClose,
  onConfirm,
}: {
  action: { type: AgentLifecycleAction; agent: AgentRecord } | null
  isSubmitting: boolean
  error: ApiError | null
  onClose: () => void
  onConfirm: (
    action: AgentLifecycleAction,
    agent: AgentRecord,
    payload: { reason?: string },
  ) => Promise<void>
}) {
  const { t } = useTranslation()
  const [reason, setReason] = useState('')
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS)
  const [countdownStarted, setCountdownStarted] = useState(false)

  const lifecycleCopy = useMemo(
    () => ({
      suspend: {
        title: t('agents.dialogs.lifecycle.suspend.title'),
        description: t('agents.dialogs.lifecycle.suspend.description'),
        alertTitle: t('agents.dialogs.lifecycle.suspend.alertTitle'),
        alertDescription: t('agents.dialogs.lifecycle.suspend.alertDescription'),
        confirmLabel: t('agents.dialogs.lifecycle.suspend.confirmLabel'),
        destructive: true,
      },
      reactivate: {
        title: t('agents.dialogs.lifecycle.reactivate.title'),
        description: t('agents.dialogs.lifecycle.reactivate.description'),
        alertTitle: t('agents.dialogs.lifecycle.reactivate.alertTitle'),
        alertDescription: t('agents.dialogs.lifecycle.reactivate.alertDescription'),
        confirmLabel: t('agents.dialogs.lifecycle.reactivate.confirmLabel'),
        destructive: false,
      },
    }),
    [t],
  )

  const copy = action ? lifecycleCopy[action.type] : lifecycleCopy.suspend
  const assignedProgramsCount =
    action?.agent.assigned_programs_count ?? action?.agent.assigned_programs?.length ?? 0
  const requiresCountdown = action?.type === 'suspend' && assignedProgramsCount > 0

  useEffect(() => {
    setReason('')
    setCountdown(COUNTDOWN_SECONDS)
    setCountdownStarted(false)
  }, [action])

  useEffect(() => {
    if (!action || !requiresCountdown || isSubmitting || !countdownStarted) {
      return
    }

    if (countdown <= 0) {
      return
    }

    const timer = window.setTimeout(() => {
      setCountdown((current) => Math.max(0, current - 1))
    }, 1000)

    return () => window.clearTimeout(timer)
  }, [action, countdown, countdownStarted, isSubmitting, requiresCountdown])

  const suspendWarningDescription = useMemo(() => {
    if (!requiresCountdown) {
      return t('agents.dialogs.lifecycle.assignedPrograms.noProgramsWarning')
    }

    if (!countdownStarted) {
      return t('agents.dialogs.lifecycle.assignedPrograms.countdownNotStarted', {
        count: assignedProgramsCount.toLocaleString(getAppLocale()),
        plural: assignedProgramsCount > 1 ? 's' : '',
      })
    }

    return t('agents.dialogs.lifecycle.assignedPrograms.countdownActive', {
      count: assignedProgramsCount.toLocaleString(getAppLocale()),
      plural: assignedProgramsCount > 1 ? 's' : '',
      countdown,
    })
  }, [assignedProgramsCount, countdown, countdownStarted, requiresCountdown, t])

  const suspendButtonDisabled =
    isSubmitting ||
    !action ||
    (action.type === 'suspend' && !reason.trim()) ||
    (action?.type === 'suspend' && requiresCountdown && countdownStarted && countdown > 0)

  return (
    <Dialog
      open={Boolean(action)}
      onOpenChange={(isOpen) => {
        if (!isOpen && !isSubmitting) onClose()
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{copy.title}</DialogTitle>
          <DialogDescription>{copy.description}</DialogDescription>
        </DialogHeader>

        <Alert
          variant={copy.destructive ? 'destructive' : 'default'}
          className={
            !copy.destructive
              ? 'border-emerald-500/25 bg-emerald-500/10 text-emerald-900 dark:text-emerald-300'
              : undefined
          }
        >
          {copy.destructive ? <AlertTriangleIcon /> : <InfoIcon />}
          <AlertTitle>{copy.alertTitle}</AlertTitle>
          <AlertDescription>{copy.alertDescription}</AlertDescription>
        </Alert>

        {action?.type === 'suspend' ? (
          <div className="space-y-4">
            <Alert className="border-amber-500/25 bg-amber-500/10 text-amber-950 dark:text-amber-200">
              <UserX />
              <AlertTitle>{t('agents.dialogs.lifecycle.assignedPrograms.title')}</AlertTitle>
              <AlertDescription>{suspendWarningDescription}</AlertDescription>
            </Alert>

            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground" htmlFor="agent-suspend-reason">
                {t('agents.dialogs.lifecycle.reason.label')}
              </label>
              <Textarea
                id="agent-suspend-reason"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                rows={4}
                placeholder={t('agents.dialogs.lifecycle.reason.placeholder')}
              />
              <p className="text-xs text-muted-foreground">
                {t('agents.dialogs.lifecycle.reason.hint')}
              </p>
            </div>
          </div>
        ) : (
          <Alert className="border-emerald-500/25 bg-emerald-500/10 text-emerald-900 dark:text-emerald-300">
            <UserCheck />
            <AlertTitle>{t('agents.dialogs.lifecycle.reactivationAlert.title')}</AlertTitle>
            <AlertDescription>
              {t('agents.dialogs.lifecycle.reactivationAlert.description')}
            </AlertDescription>
          </Alert>
        )}

        {error ? <p className="text-sm text-destructive">{error.message}</p> : null}

        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline" disabled={isSubmitting}>
              {t('agents.dialogs.lifecycle.cancel')}
            </Button>
          </DialogClose>
          <Button
            type="button"
            variant={copy.destructive ? 'destructive' : 'default'}
            disabled={suspendButtonDisabled}
            onClick={() => {
              if (!action) return

              if (action.type === 'suspend' && requiresCountdown && !countdownStarted) {
                setCountdownStarted(true)
                return
              }

              void onConfirm(action.type, action.agent, {
                reason: action.type === 'suspend' ? reason.trim() : undefined,
              })
            }}
          >
            {isSubmitting
              ? t('agents.dialogs.lifecycle.processing')
              : action?.type === 'suspend' && requiresCountdown && countdownStarted && countdown > 0
                ? t('agents.dialogs.lifecycle.waitSeconds', { countdown })
                : copy.confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
